/**
 * useNoteAutoSave — debounced (800ms) plain-text autosave for the open note.
 *
 *   idle → saving → saved, with the latest ETag tracked across saves and a
 *   one-shot 412 recovery (refetch fresh ETag → retry) for optimistic-concurrency
 *   conflicts.
 *
 * The component owns the textarea `draft`; this hook only persists it. A pending
 * save is FLUSHED (not dropped) when the active note changes or the component
 * unmounts, so switching/closing within the debounce window never loses edits.
 * Saves carry their note id + etag explicitly and only write back to the shared
 * baseline when that note is still the current one, so a flush for the note you
 * just left can't corrupt the note you switched to.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useSession } from '@/features/auth/hooks/useSession';
import { useUpdateObject } from '@/features/objects/hooks';
import { useToast } from '@/app/providers/ToastContext';
import { titleForPersist, type NoteObject } from '../notesShared';

export type SaveStatus = 'idle' | 'saving' | 'saved';

const AUTOSAVE_DELAY_MS = 800;

export function useNoteAutoSave(params: {
  note: NoteObject | null;
  draft: string;
  /** The note id the current `draft` belongs to (set by the parent when it loads a note). */
  draftNoteId: string | null;
  moduleId: string;
}): { status: SaveStatus; savedAt: number | null } {
  const { note, draft, draftNoteId, moduleId } = params;
  const { token } = useSession();
  const { showToast } = useToast();
  const updateNote = useUpdateObject<NoteObject>('note', undefined, moduleId);

  const [status, setStatus] = useState<SaveStatus>('idle');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const etagRef = useRef<string>('');
  const savedBodyRef = useRef<string>('');
  const noteIdRef = useRef<string | null>(null);
  // The latest scheduled-but-not-yet-run save, so a note switch / unmount can
  // flush it instead of cancelling it.
  const pendingRef = useRef<{ id: string; body: string } | null>(null);
  // mutateAsync is stable across renders, but mirror into a ref so the stable
  // performSave callback never closes over a stale mutation instance.
  const mutateRef = useRef(updateNote.mutateAsync);
  mutateRef.current = updateNote.mutateAsync;

  // Reset the save baseline whenever the active note changes (new note opened).
  useEffect(() => {
    noteIdRef.current = note?.id ?? null;
    etagRef.current = note?._etag || (note?.version != null ? String(note.version) : '');
    savedBodyRef.current = note?.body ?? '';
    setStatus('idle');
    setSavedAt(null);
    // Only re-run when the active note identity changes.
  }, [note?.id]);

  // Persist `body` for note `id` using `startEtag`. Only writes back to the
  // shared refs/status when `id` is STILL the current note, so a flush of the
  // note you just left never poisons the note you switched to.
  const performSave = useCallback(
    async (id: string, body: string, startEtag: string) => {
      const patch = { title: titleForPersist(body), body } as Partial<NoteObject>;
      const isCurrent = () => noteIdRef.current === id;
      const apply = (newEtag: string | undefined) => {
        if (!isCurrent()) return;
        etagRef.current = newEtag || etagRef.current;
        savedBodyRef.current = body;
        setStatus('saved');
        setSavedAt(Date.now());
      };
      try {
        const updated = await mutateRef.current({ id, etag: startEtag, patch });
        apply(updated._etag);
      } catch (err) {
        if ((err as { status?: number })?.status === 412) {
          // Optimistic-concurrency conflict: pull the fresh ETag and retry once.
          try {
            const { object } = await api.objects.getObject(token, 'note', id);
            const fresh = (object as NoteObject)._etag || startEtag;
            const updated = await mutateRef.current({ id, etag: fresh, patch });
            apply(updated._etag);
            return;
          } catch {
            /* fall through to the generic failure path */
          }
        }
        if (isCurrent()) setStatus('idle');
        showToast('Could not save note', 'error');
      }
    },
    [token, showToast],
  );

  // Debounced save: schedule a save whenever the draft diverges from what's
  // stored. Guarded on draftNoteId === note.id so we never save while the draft
  // still belongs to the previously-open note (draft lags by one render on switch).
  useEffect(() => {
    if (!note || draftNoteId !== note.id) {
      return;
    }
    if (draft === savedBodyRef.current) {
      pendingRef.current = null;
      return;
    }

    setStatus('saving');
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const id = note.id;
    const body = draft;
    pendingRef.current = { id, body };
    timeoutRef.current = setTimeout(() => {
      pendingRef.current = null;
      void performSave(id, body, etagRef.current);
    }, AUTOSAVE_DELAY_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [draft, draftNoteId, note?.id, performSave]);

  // Flush a still-pending save when the active note changes or the component
  // unmounts. The cleanup runs BEFORE the reset effect's next run, so etagRef
  // still holds the leaving note's etag.
  useEffect(() => {
    return () => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      void performSave(pending.id, pending.body, etagRef.current);
    };
  }, [note?.id, performSave]);

  return { status, savedAt };
}
